/**
 * Price Range — dual range slider with synced number inputs.
 * Dispatches 'change' events on the number inputs so collection.js picks them up.
 */
class PriceRangeComponent extends HTMLElement {
  #rangeMin = null;
  #rangeMax = null;
  #inputMin = null;
  #inputMax = null;
  #fill = null;
  #gap = 1;

  connectedCallback() {
    this.#rangeMin = this.querySelector('[data-range-min]');
    this.#rangeMax = this.querySelector('[data-range-max]');
    this.#inputMin = this.querySelector('[data-input-min]');
    this.#inputMax = this.querySelector('[data-input-max]');
    this.#fill = this.querySelector('[data-fill]');

    if (!this.#rangeMin || !this.#rangeMax) return;

    this.#rangeMin.addEventListener('input', this.#onRangeMinInput);
    this.#rangeMax.addEventListener('input', this.#onRangeMaxInput);

    // Commit on release (dispatches the change event to trigger filtering)
    this.#rangeMin.addEventListener('change', this.#onRangeMinCommit);
    this.#rangeMax.addEventListener('change', this.#onRangeMaxCommit);

    this.#inputMin?.addEventListener('change', this.#onInputMinChange);
    this.#inputMax?.addEventListener('change', this.#onInputMaxChange);

    this.#updateFill();
  }

  // Live feedback while dragging (no filter trigger)
  #onRangeMinInput = () => {
    const min = parseFloat(this.#rangeMin.value);
    const max = parseFloat(this.#rangeMax.value);
    if (min > max - this.#gap) this.#rangeMin.value = String(max - this.#gap);
    if (this.#inputMin) this.#inputMin.value = this.#rangeMin.value;
    this.#updateFill();
  };

  #onRangeMaxInput = () => {
    const min = parseFloat(this.#rangeMin.value);
    const max = parseFloat(this.#rangeMax.value);
    if (max < min + this.#gap) this.#rangeMax.value = String(min + this.#gap);
    if (this.#inputMax) this.#inputMax.value = this.#rangeMax.value;
    this.#updateFill();
  };

  // On release: sync number input value then let the native change event bubble to collection.js
  #onRangeMinCommit = () => {
    if (this.#inputMin) this.#inputMin.value = this.#rangeMin.value;
    this.#updateFill();
  };

  #onRangeMaxCommit = () => {
    if (this.#inputMax) this.#inputMax.value = this.#rangeMax.value;
    this.#updateFill();
  };

  // Number input changes
  #onInputMinChange = () => {
    let val = parseFloat(this.#inputMin.value);
    const max = parseFloat(this.#rangeMax.value);
    const absMin = parseFloat(this.#rangeMin.min);

    val = Math.max(absMin, Math.min(val, max - this.#gap));
    this.#inputMin.value = String(val);
    this.#rangeMin.value = String(val);
    this.#updateFill();
  };

  #onInputMaxChange = () => {
    let val = parseFloat(this.#inputMax.value);
    const min = parseFloat(this.#rangeMin.value);
    const absMax = parseFloat(this.#rangeMax.max);

    val = Math.max(min + this.#gap, Math.min(val, absMax));
    this.#inputMax.value = String(val);
    this.#rangeMax.value = String(val);
    this.#updateFill();
  };

  #updateFill() {
    if (!this.#fill || !this.#rangeMin || !this.#rangeMax) return;

    const min = parseFloat(this.#rangeMin.value);
    const max = parseFloat(this.#rangeMax.value);
    const absMin = parseFloat(this.#rangeMin.min);
    const absMax = parseFloat(this.#rangeMin.max);
    const range = absMax - absMin;

    if (range <= 0) return;

    this.#fill.style.left = `${((min - absMin) / range) * 100}%`;
    this.#fill.style.right = `${((absMax - max) / range) * 100}%`;
  }
}

if (!customElements.get('price-range-component')) {
  customElements.define('price-range-component', PriceRangeComponent);
}
